import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useViewMode } from '../hooks/useViewMode';
import { projectsApi, aiApi } from '../utils/api';
import { ProjectCreateForm } from '../components/marketplace/ProjectCreateForm';
import { ItemCreateForm } from '../components/marketplace/ItemCreateForm';
import { EmptyState } from '../components/shared/EmptyState';
import { Spinner } from '../components/shared/Spinner';
import { ConfirmationModal } from '../components/shared/ConfirmationModal';
import { formatCurrency } from '../utils/currency';

interface Project {
  _id: string;
  title: string;
  description?: string;
  address?: string;
  status: 'draft' | 'listed' | 'completed';
  items: any[];
  tags: string[];
  itemCount?: number;
  createdAt: string;
  updatedAt: string;
}

export const Projects: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showCreateItem, setShowCreateItem] = useState<string | null>(null);
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [viewMode, setViewMode] = useViewMode('grid');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; project: Project | null; isLoading: boolean }>({
    isOpen: false,
    project: null,
    isLoading: false
  });

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await projectsApi.list({ mine: true });
      if (response.data.success) {
        setProjects(response.data.data || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadProjects();
    }
  }, [isAuthenticated, loadProjects]);

  const handleProjectCreated = (project: Project) => {
    setProjects(prev => [project, ...prev]);
    setShowCreateProject(false);
  };

  const handleItemCreated = (item: any) => {
    // Refresh projects to get updated item counts
    loadProjects();
    setShowCreateItem(null);
  };

  const handleDeleteClick = (project: Project) => {
    setDeleteConfirm({
      isOpen: true,
      project,
      isLoading: false
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.project) return;

    setDeleteConfirm(prev => ({ ...prev, isLoading: true }));

    try {
      await projectsApi.delete(deleteConfirm.project._id);
      
      // Remove the deleted project from the state
      setProjects(prev => prev.filter(p => p._id !== deleteConfirm.project!._id));
      
      // Close the modal
      setDeleteConfirm({
        isOpen: false,
        project: null,
        isLoading: false
      });
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to delete project');
      setDeleteConfirm(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({
      isOpen: false,
      project: null,
      isLoading: false
    });
  };

  const handleProjectSelect = (projectId: string) => {
    const newSelected = new Set(selectedProjects);
    if (newSelected.has(projectId)) {
      newSelected.delete(projectId);
    } else {
      newSelected.add(projectId);
    }
    setSelectedProjects(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const handleSelectAll = () => {
    if (selectedProjects.size === projects.length) {
      setSelectedProjects(new Set());
      setShowBulkActions(false);
    } else {
      setSelectedProjects(new Set(projects.map(p => p._id)));
      setShowBulkActions(true);
    }
  };

  const handleBulkDelete = () => {
    // TODO: Implement bulk delete functionality
    console.log('Bulk deleting projects:', Array.from(selectedProjects));
  };

  const handleBulkExport = () => {
    // TODO: Implement bulk export functionality
    console.log('Bulk exporting projects:', Array.from(selectedProjects));
  };

  const handleAiGenerateProject = async () => {
    if (aiGenerating) return;
    
    setAiGenerating(true);
    try {
      // Generate project data using AI
      const response = await aiApi.generateTestProject();
      
      if (response.data.success && response.data.data) {
        const projectData = response.data.data;
        
        // Create the project using the generated data
        const createResponse = await projectsApi.create({
          title: projectData.title,
          description: projectData.description,
          address: projectData.address,
          tags: projectData.tags,
          status: 'draft' as const
        });
        
        if (createResponse.data.success && createResponse.data.data?.project) {
          // Add the new project to the list
          setProjects(prev => [createResponse.data.data!.project, ...prev]);
        } else {
          console.error('Failed to create AI-generated project');
        }
      }
    } catch (error) {
      console.error('Failed to generate AI project:', error);
      // You could add a toast notification here
    } finally {
      setAiGenerating(false);
    }
  };

  const handleProjectClick = (project: Project, event: React.MouseEvent) => {
    // Check if clicking on the checkbox area (top-left corner)
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;
    
    // If clicking in the top-left 48x48px area (checkbox + padding), toggle selection
    if (clickX <= 48 && clickY <= 48) {
      handleProjectSelect(project._id);
      return;
    }
    
    // If project is already selected and we have selections, toggle selection
    if (selectedProjects.has(project._id) && selectedProjects.size > 0) {
      handleProjectSelect(project._id);
      return;
    }
    
    // If we have other projects selected, add this one to selection
    if (selectedProjects.size > 0) {
      handleProjectSelect(project._id);
      return;
    }
    
    // Otherwise, navigate to project details (you can implement this later)
    console.log('Navigate to project:', project._id);
  };

  if (!isAuthenticated) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Projects</h1>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            Please log in to view and manage your projects.
          </p>
          <button className="btn-primary">
            Log In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          {/* Count removed as requested */}
        </div>

        <div className="flex items-center space-x-3">
          {/* New Project Button */}
        <button 
          onClick={() => setShowCreateProject(true)}
            className="btn-primary p-2"
            title="New Project"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
          
          {/* AI Test Button */}
          <button
            onClick={handleAiGenerateProject}
            disabled={aiGenerating}
            className={`btn-primary p-2 ${
              aiGenerating ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title="AI Test"
          >
            {aiGenerating ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            )}
          </button>

          {/* View Mode Toggle */}
          <div className="flex items-center space-x-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grid'
                  ? 'bg-white dark:bg-slate-700 shadow-sm'
                  : 'hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-slate-700 shadow-sm'
                  : 'hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
        </button>
      </div>

          {/* Select All Button (only show when projects are selected) */}
          {selectedProjects.size > 0 && (
            <>
              <button
                onClick={handleSelectAll}
                className="btn-secondary"
              >
                {selectedProjects.size === projects.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={() => {
                  setSelectedProjects(new Set());
                  setShowBulkActions(false);
                }}
                className="btn-secondary"
              >
                Clear Selection
              </button>
            </>
          )}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedProjects.size > 0 && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
              {selectedProjects.size} project{selectedProjects.size !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleBulkExport}
                className="btn-secondary text-sm"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export
              </button>
              <button
                onClick={handleBulkDelete}
                className="btn-destructive text-sm"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button 
            onClick={loadProjects}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      ) : projects.length === 0 ? (
        <EmptyState
          icon={
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
          title="No projects yet"
          description="Create your first project to start organizing your deconstruction work and listing items for sale."
          action={
            <button 
              onClick={() => setShowCreateProject(true)}
              className="btn-primary"
            >
              Create Your First Project
            </button>
          }
        />
      ) : (
        <>
          {/* Projects Grid/List */}
          {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
                <div
                  key={project._id}
                  className={`relative card p-6 cursor-pointer transition-all duration-200 ${
                    selectedProjects.has(project._id)
                      ? 'ring-2 ring-primary-500 ring-offset-2 ring-offset-white dark:ring-offset-slate-900'
                      : 'hover:shadow-lg'
                  }`}
                  onClick={(e) => handleProjectClick(project, e)}
                >
                  {/* Selection Checkbox */}
                  <div className="absolute top-4 left-4 z-10">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 shadow-sm ${
                      selectedProjects.has(project._id)
                        ? 'bg-primary-500 border-primary-500 scale-110'
                        : 'bg-white/90 border-white/90 backdrop-blur-sm hover:bg-white hover:scale-105'
                    }`}>
                      {selectedProjects.has(project._id) && (
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start justify-between mb-4 ml-8">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2 line-clamp-2">
                    {project.title}
                  </h3>
                  {project.description && (
                    <p className="text-slate-600 dark:text-slate-400 text-sm mb-3 line-clamp-3">
                      {project.description}
                    </p>
                  )}
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  project.status === 'draft' 
                    ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300'
                    : project.status === 'listed'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                }`}>
                  {project.status}
                </span>
              </div>

              {project.address && (
                    <div className="flex items-center text-sm text-slate-500 dark:text-slate-400 mb-3 ml-8">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  {project.address}
                </div>
              )}

                  <div className="flex items-center justify-between mb-4 ml-8">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  {/* Count removed as requested */}
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  Created {new Date(project.createdAt).toLocaleDateString()}
                </span>
              </div>

              {project.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4 ml-8">
                  {project.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded dark:bg-slate-700 dark:text-slate-300"
                    >
                      {tag}
                    </span>
                  ))}
                  {project.tags.length > 3 && (
                    <span className="px-2 py-1 text-xs text-slate-500">
                      +{project.tags.length - 3} more
                    </span>
                  )}
                </div>
              )}

                  <div className="flex space-x-2 ml-8">
                <Link
                  to={`/projects/${project._id}`}
                  className="btn-secondary flex-1 text-center text-sm"
                      onClick={(e) => e.stopPropagation()}
                >
                  View Details
                </Link>
                <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCreateItem(project._id);
                      }}
                  className="btn-primary flex-1 text-sm"
                >
                  Add Item
                </button>
                <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(project);
                      }}
                      className="btn-destructive text-sm px-3 py-2"
                      title="Delete Project"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {projects.map((project) => (
                <div
                  key={project._id}
                  className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
                    selectedProjects.has(project._id)
                      ? 'border-primary-200 bg-primary-50 dark:border-primary-800 dark:bg-primary-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                  onClick={(e) => handleProjectClick(project, e)}
                >
                  {/* Selection Checkbox */}
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 shadow-sm ${
                    selectedProjects.has(project._id)
                      ? 'bg-primary-500 border-primary-500 scale-110'
                      : 'bg-white border-slate-300 dark:border-slate-600 dark:bg-slate-700 hover:scale-105'
                  }`}>
                    {selectedProjects.has(project._id) && (
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>

                  {/* Project Icon */}
                  <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>

                  {/* Project Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                        {project.title}
                      </h3>
                      <span className={`px-2 py-1 text-xs rounded-full ml-2 ${
                        project.status === 'draft' 
                          ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300'
                          : project.status === 'listed'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                      }`}>
                        {project.status}
                      </span>
                    </div>
                    
                    {project.description && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mb-2">
                        {project.description}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between text-sm text-slate-500 dark:text-slate-400">
                      <div className="flex items-center space-x-4">
                        {project.address && (
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                            <span className="truncate">{project.address}</span>
                          </div>
                        )}
                        {/* Count removed as requested */}
                      </div>
                      <span>Created {new Date(project.createdAt).toLocaleDateString()}</span>
                    </div>

                    {project.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {project.tags.slice(0, 4).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                        {project.tags.length > 4 && (
                          <span className="px-2 py-1 text-xs text-slate-500">
                            +{project.tags.length - 4} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <Link
                      to={`/projects/${project._id}`}
                      className="btn-secondary text-sm px-3 py-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      View
                    </Link>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCreateItem(project._id);
                      }}
                      className="btn-primary text-sm px-3 py-2"
                    >
                      Add Item
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(project);
                      }}
                  className="btn-destructive text-sm px-3 py-2"
                  title="Delete Project"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
          )}
        </>
      )}

      {/* Create Project Modal */}
      {showCreateProject && (
        <ProjectCreateForm
          onSuccess={handleProjectCreated}
          onCancel={() => setShowCreateProject(false)}
        />
      )}

      {/* Create Item Modal */}
      {showCreateItem && (
        <ItemCreateForm
          projectId={showCreateItem}
          onSuccess={handleItemCreated}
          onCancel={() => setShowCreateItem(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        title="Delete Project"
        message={`Are you sure you want to delete "${deleteConfirm.project?.title}"? This action cannot be undone and will also delete all items associated with this project.`}
        confirmText="Delete Project"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isLoading={deleteConfirm.isLoading}
        variant="danger"
      />
    </div>
  );
};
